import React from 'react';
import { IntegrationFlow, IntegrationCanvasNode, NodeExecutionStatus } from '../types';

interface IntegrationConnectionLinesProps {
  flows: IntegrationFlow[];
  nodeStatuses?: Record<string, NodeExecutionStatus>;
  isExecuting?: boolean;
}

/*
 * The wires between integration nodes. Geometry lives in the canvas's LTR
 * pixel space and matches the node sizes in IntegrationNode (trigger 104×90,
 * regular 180×68) — do not change one without the other. SVG strokes cannot
 * read Tailwind classes, so colours come from CSS variables directly.
 */
export const IntegrationConnectionLines: React.FC<IntegrationConnectionLinesProps> = ({
  flows,
  nodeStatuses,
  isExecuting = false,
}) => {
  const ink = 'var(--foreground)';
  const live = 'var(--success)';
  const dim = 'var(--muted-foreground)';

  const getStatus = (nodeId: string): NodeExecutionStatus =>
    nodeStatuses?.[nodeId] ?? 'idle';

  const renderLine = (from: IntegrationCanvasNode, to: IntegrationCanvasNode, index: number, allConfigured: boolean) => {
    const fromWidth = from.type === 'trigger' ? 104 : 180;
    const fromHeight = from.type === 'trigger' ? 90 : 68;
    const toHeight = to.type === 'trigger' ? 90 : 68;
    const fromX = from.x + fromWidth;
    const fromY = from.y + fromHeight / 2;
    const toX = to.x;
    const toY = to.y + toHeight / 2;

    const midX = (fromX + toX) / 2;
    const path = `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`;

    // Determine line style based on execution state
    if (isExecuting && nodeStatuses) {
      const fromStatus = getStatus(from.id);
      const toStatus = getStatus(to.id);

      // Both completed → solid success
      if (fromStatus === 'completed' && toStatus === 'completed') {
        return (
          <g key={`${from.id}-${to.id}`}>
            <path d={path} fill="none" stroke={live} strokeWidth="2" opacity="0.7" />
            <circle cx={fromX} cy={fromY} r="4" fill={live} opacity="0.7" />
            <circle cx={toX} cy={toY} r="4" fill={live} opacity="0.7" />
          </g>
        );
      }

      // Completed → running: fast animated dash in ink
      if (fromStatus === 'completed' && toStatus === 'running') {
        return (
          <g key={`${from.id}-${to.id}`}>
            <path
              d={path}
              fill="none"
              stroke={ink}
              strokeWidth="2"
              strokeDasharray="5,5"
              opacity="0.7"
              style={{
                animation: 'executionFlowAnimation 1s linear infinite',
              }}
            />
            <circle cx={fromX} cy={fromY} r="4" fill={live} opacity="0.7" />
            <circle
              cx={toX}
              cy={toY}
              r="4"
              fill={ink}
              opacity="0.7"
              style={{
                animation: 'executionPulse 1s ease-in-out infinite',
              }}
            />
          </g>
        );
      }

      // After running node or between idle nodes → dim dashed
      return (
        <g key={`${from.id}-${to.id}`}>
          <path
            d={path}
            fill="none"
            stroke={dim}
            strokeWidth="2"
            strokeDasharray="5,5"
            opacity="0.15"
          />
          <circle cx={fromX} cy={fromY} r="4" fill={dim} opacity="0.15" />
          <circle cx={toX} cy={toY} r="4" fill={dim} opacity="0.15" />
        </g>
      );
    }

    // Default non-executing behavior
    return (
      <g key={`${from.id}-${to.id}`}>
        <path
          d={path}
          fill="none"
          stroke={ink}
          strokeWidth="2"
          strokeDasharray="5,5"
          opacity={allConfigured ? '0.4' : '0.3'}
          style={allConfigured ? {
            animation: 'flowAnimation 4s linear infinite',
            animationDelay: `${index * 0.6}s`,
          } : {}}
        />
        <circle
          cx={fromX}
          cy={fromY}
          r="4"
          fill={ink}
          opacity={allConfigured ? '0.5' : '0.3'}
        />
        <circle
          cx={toX}
          cy={toY}
          r="4"
          fill={ink}
          opacity={allConfigured ? '0.5' : '0.3'}
          style={allConfigured ? {
            animation: 'pulseAnimation 4s ease-in-out infinite',
            animationDelay: `${index * 0.6}s`,
          } : {}}
        />
      </g>
    );
  };

  return (
    <svg className="pointer-events-none absolute top-0 left-0 z-0 h-full w-full overflow-visible">
      <style>{`
        @keyframes flowAnimation { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -100; } }
        @keyframes pulseAnimation { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.6; } }
        @keyframes executionFlowAnimation { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -100; } }
        @keyframes executionPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 0.9; } }
      `}</style>
      {flows.map((flow) => {
        const sortedNodes = [...flow.nodes].sort((a, b) => a.x - b.x);
        const allConfigured = sortedNodes.length > 0 && sortedNodes.every(
          (node) => node.config && Object.keys(node.config).length > 0,
        );

        return sortedNodes.map((node, index) => {
          if (index < sortedNodes.length - 1) {
            return renderLine(node, sortedNodes[index + 1], index, allConfigured);
          }
          return null;
        });
      })}
    </svg>
  );
};
